import React, { useState } from 'react';
import { CheckCircle, X, Check, ArrowRight, Info } from '@phosphor-icons/react';
import TicketSelector from './TicketSelector';
import { submitNameChange, saveData } from '../api/connectionRegistryApi';
import { useLoginName } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import SectionLock from './SectionLock';

const onlyLetters = (value) => value.replace(/[^A-Za-z ]/g, '');

function NameChangeForm({ connectionId }) {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);
  const [record, setRecord] = useState(null);
  const [newName, setNewName] = useState('');
  const [newSurname, setNewSurname] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingOldFullName, setPendingOldFullName] = useState('');
  const [pendingNewFullName, setPendingNewFullName] = useState('');
  const [pendingReason, setPendingReason] = useState('');

  const newMailingName = `${newName} ${newSurname}`.trim();

  const nameInvalid = touched && !newName;
  const surnameInvalid = touched && !newSurname;
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const isValid = !!record && !!newName && !!newSurname && reason.trim().length >= 10 && reason.length <= 200;

  /** Step 1: validate, stage the pending change, open the confirm modal. */
  const confirmSave = () => {
    setTouched(true);
    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    setPendingOldFullName(`${record.oldName} ${record.oldSurname}`.trim());
    setPendingNewFullName(`${newName} ${newSurname}`.trim());
    setPendingReason(reason);
    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  /** Step 2: fires only after the user clicks "Yes, Save" in the modal. */
  const doSave = async () => {
    setSaving(true);
    try {
      await submitNameChange(connectionId, {
        contactId: record.contactId,
        branchCode: record.branchCode,
        oldName: record.oldName,
        oldSurname: record.oldSurname,
        oldMailingName: record.oldMailingName,
        newName,
        newSurname,
        reason,
      });
      try {
        await saveData([{
          schemaName: record.branchCode ?? null,
          loginName,
          loginTime: new Date().toISOString(),
          changeType: 'NAME_CHANGE',
          oldData: {
            contactId: record.contactId,
            branchCode: record.branchCode,
            name: record.oldName,
            surname: record.oldSurname,
            mailingName: record.oldMailingName,
          },
          newData: {
            contactId: record.contactId,
            branchCode: record.branchCode,
            name: newName,
            surname: newSurname,
            mailingName: newMailingName,
          },
          reason: reason.trim(),
        }]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', `Name changed from ${pendingOldFullName} to ${pendingNewFullName}.`, true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Name updated, but audit log failed to save.', true);
        handleClear();
      }
    } catch (e) {
      setSaving(false);
      setShowConfirmModal(false);
      showToast('error', 'Save Failed', e?.response?.data?.detail || 'Could not save this change. Please try again.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setRecord(null);
    setNewName('');
    setNewSurname('');
    setReason('');
    setTouched(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="name-change-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="name-change-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
        <div style={{ marginBottom: '20px', fontSize: '16px', fontWeight: '700', color: '#111827' }}>Name Change</div>

        <TicketSelector key={formKey} connectionId={connectionId} onRecordChange={setRecord} onNoData={(msg) => showToast('warn', 'No Data Found', msg)} onError={(msg) => showToast('error', 'Load Failed', msg)} />

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!record} hint="Select a ticket in section A first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Update Name Details</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Review current details on the left, enter new details on the right</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '8px' }}>Current Details (Read-Only)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Name</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={record?.oldName || ''} readOnly data-testid="name-old-name" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Surname</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={record?.oldSurname || ''} readOnly />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Mailing Name</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={record?.oldMailingName || ''} readOnly />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Status</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={record?.oldStatus || ''} readOnly />
              </div>
            </div>
          </div>

          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', marginBottom: '8px' }}>New Details (Editable)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Name *</label>
                <input
                  className="input-field"
                  style={{ fontSize: '13px', borderColor: nameInvalid ? '#DC2626' : undefined }}
                  value={newName}
                  maxLength={50}
                  placeholder="Enter new name"
                  onChange={(e) => setNewName(onlyLetters(e.target.value))}
                  data-testid="name-new-name"
                />
                {nameInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Name is required</div>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Surname *</label>
                <input
                  className="input-field"
                  style={{ fontSize: '13px', borderColor: surnameInvalid ? '#DC2626' : undefined }}
                  value={newSurname}
                  maxLength={20}
                  placeholder="Enter new surname"
                  onChange={(e) => setNewSurname(onlyLetters(e.target.value))}
                  data-testid="name-new-surname"
                />
                {surnameInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Surname is required</div>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Mailing Name (Auto)</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={newMailingName} readOnly data-testid="name-new-mailing" />
              </div>
            </div>
          </div>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!record || !newName || !newSurname} hint="Enter the new name and surname in section B first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Change</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={3}
          maxLength={200}
          placeholder="Explain why this name change is being made..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="name-reason"
        />
        <div style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : '#9CA3AF', marginTop: '2px', textAlign: 'right' }}>
          {reason.length} / 200
        </div>
        {reasonInvalid && <div style={{ fontSize: '11px', color: '#DC2626' }}>Minimum 10 characters required</div>}
        </SectionLock>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
          <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="name-clear-button">Clear</button>
          <button type="button" onClick={confirmSave} className="btn-primary" disabled={saving} data-testid="name-save-button">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="name-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CheckCircle size={18} color="#002FA7" weight="bold" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Name Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to change this subscriber&apos;s name. Please review before confirming.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>From</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingOldFullName || '—'}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>To</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{pendingNewFullName || '—'}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="name-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={doSave} disabled={saving} className="btn-primary" data-testid="name-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NameChangeForm;